import React, { useState, useEffect } from "react";
import Text from "../Text";
import Img from "../Img";
import imgLeft from '../../assets/img/img_left.svg'; // or your uploaded room image
import imgRight from '../../assets/img/img_right.svg'; // or your uploaded room image
import { useTranslation } from "react-i18next";


type FlexShopCarouselProps = {
  flexItems: any[]; // Replace 'any' with a more specific type if available
  [key: string]: any;
};

function FlexShopCarousel({ flexItems, ...props }: FlexShopCarouselProps) {
  const { t, i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const nextSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex + 1) % flexItems.length);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const prevSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex - 1 + flexItems.length) % flexItems.length);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  // Auto-scroll every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full flex flex-col items-center" {...props}>
      <Text
        size="text8xl"
        as="p"
        className="text-[40px] font-normal !text-white-a700 lg:text-[34px] md:text-[34px] sm:text-[32px] mb-4"
        style={{ fontSize: '3em' }}

      >
        {t("home.carousel.flex.title")}
      </Text>
      
      <Text
        as="p"
        className="text-[20px] font-normal !text-white-a700 text-center max-w-4xl mb-20 px-4 font-upheaval flex-shop-description"
        style={{ fontSize: '1.5em', marginBottom: '100px' }}

     >
        {t("home.carousel.flex.subtitle_p1")}<span className="highlight">{t("home.carousel.flex.subtitle_p2")}</span>{t("home.carousel.flex.subtitle_p3")}
      </Text>
      
      <div className="relative w-full max-w-6xl flex flex-col justify-center items-center isolate">
        {/* Carousel main container */}
        <div className="w-full flex items-center justify-center px-4 relative">
          {/* Left Arrow */}
          <button 
            onClick={prevSlide} 
            className="absolute left-0 z-30 p-2 cursor-pointer transform -translate-x-1/2 transition-opacity duration-300 hover:opacity-80"
            aria-label="Previous item"
            style={{marginLeft: '24px'}}
          >
            <Img
              src={imgLeft} 
              alt="Previous" 
              className="h-[52px] w-[30px] object-contain" 
            />
          </button>
          
          {/* Main card - always centered and prominent */}
          <div className="flex justify-center items-center gap-10 px-4 overflow-visible w-full relative" style={{gap: '3rem'}}>
            {[-1, 0, 1].map((offset) => {
              const index = (currentIndex + offset + flexItems.length) % flexItems.length;
              const isMain = offset === 0;
              
              // Different styling for main card vs side cards with adjusted z-index values
              const cardClass = isMain 
                ? "z-10 transform scale-125 w-[320px]" 
                : offset < 0 
                  ? "z-5 opacity-60 -mr-16 w-[260px]" 
                  : "z-5 opacity-60 -ml-16 w-[260px]";
              
              return (
                <div 
                  key={index}
                  className={`transition-all duration-500 ease-in-out ${cardClass}`}
                >
                  <div className={`flex-item-card-container ${isMain ? 'main-card' : 'side-card'}`}>
                    <div className="pixel-flex-container">
                      <div className="pixel-flex-box">
                        <Img 
                          src={flexItems[index].image} 
                          alt={flexItems[index].name}
                          style={{ width: 'auto', height: '250px' }}
                          className="w-auto object-contain mb-3" 
                        />
                        <Text 
                          as="p" 
                          className="text-[28px] font-normal font-upheaval !text-white-a700 text-center"
                          style={{ fontSize: '2em' }}
                        >
                          {flexItems[index].name}
                        </Text>
                        
                        {/* Price tag with animated glow */}
                        <div className="flex items-center justify-center w-full px-4">
                          <div className="pixel-price-tag">
                            <Text 
                              as="p" 
                              className="text-[18px] font-normal font-upheaval !text-white-a700 text-center"
                              style={{ fontSize: '1.3em' }}
                        >
                              {flexItems[index].price || "5000 $MILK"}
                            </Text>
                          </div>
                        </div>
                      </div>
                      <div className="pixel-scanlines"></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Right Arrow */}
          <button 
            onClick={nextSlide} 
            className="absolute right-0 z-30 p-2 cursor-pointer transform translate-x-1/2 transition-opacity duration-300 hover:opacity-80"
            aria-label="Next item"
          >
            <Img 
              src={imgRight} 
              alt="Next" 
              className="h-[52px] w-[30px] object-contain" 
            />
          </button>
        </div>
        
        {/* Navigation dots */}
        <div className="flex justify-center gap-3 mt-16">
          {flexItems.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                setIsAnimating(true);
                setTimeout(() => setIsAnimating(false), 500);
              }}
              className={`w-3 h-3 rounded-full cursor-pointer transition-all duration-500 ease-in-out ${
                index === currentIndex ? "bg-[#507c83] transform scale-125" : "bg-gray-600 opacity-40"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default FlexShopCarousel;
export { FlexShopCarousel }; 